import { Phone, Store } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode, SVGProps } from "react";
import { WhatsAppIcon } from "@/components/icons/whatsapp-icon";
import type { SalesChannel } from "./orders-api";
import type { TranslationKey } from "@/i18n/dictionaries";

/*
 * A mark per channel, so an order says where it came from at a glance.
 *
 * The brand marks are drawn here rather than imported: lucide dropped its
 * brand icons, and these are the five places our orders actually arrive from.
 * Each one takes its colour from `currentColor`, so the tile below decides the
 * hue and dark mode needs no second set.
 */

function FacebookIcon(props: SVGProps<SVGSVGElement>): ReactNode {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z" />
    </svg>
  );
}

function TiktokIcon(props: SVGProps<SVGSVGElement>): ReactNode {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12.53.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75a7.6 7.6 0 0 1-1.35 3.94c-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07Z" />
    </svg>
  );
}

function InstagramIcon(props: SVGProps<SVGSVGElement>): ReactNode {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.8 3.8 0 0 1-1.38-.9 3.8 3.8 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.3-1.46.72-2.13 1.38A5.9 5.9 0 0 0 .63 4.14c-.3.76-.5 1.64-.56 2.91C.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.3.79.72 1.46 1.38 2.13a5.9 5.9 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.9 5.9 0 0 0 2.13-1.38 5.9 5.9 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 0 0-1.38-2.13A5.9 5.9 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm7.85-10.4a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0Z" />
    </svg>
  );
}

/** What each channel looks like: its label, its mark, and the wash behind it. */
export const SALES_CHANNEL_META: Readonly<
  Record<
    SalesChannel,
    {
      readonly labelKey: TranslationKey;
      readonly Icon: LucideIcon | ((props: SVGProps<SVGSVGElement>) => ReactNode);
      readonly toneClassName: string;
    }
  >
> = {
  facebook: {
    labelKey: "orders.channel.facebook",
    Icon: FacebookIcon,
    toneClassName: "bg-[#1877F2]/10 text-[#1877F2]",
  },
  whatsapp: {
    labelKey: "orders.channel.whatsapp",
    Icon: WhatsAppIcon,
    toneClassName: "bg-[#25D366]/10 text-[#128C7E] dark:text-[#25D366]",
  },
  tiktok: {
    labelKey: "orders.channel.tiktok",
    Icon: TiktokIcon,
    toneClassName: "bg-foreground/10 text-foreground",
  },
  instagram: {
    labelKey: "orders.channel.instagram",
    Icon: InstagramIcon,
    toneClassName: "bg-[#E4405F]/10 text-[#E4405F]",
  },
  phone: {
    labelKey: "orders.channel.phone",
    Icon: Phone,
    toneClassName: "bg-info/10 text-info",
  },
  storefront: {
    labelKey: "orders.channel.storefront",
    Icon: Store,
    toneClassName: "bg-primary/10 text-primary",
  },
};

/** Whether a value the API sent is a channel we know how to show. */
export function isKnownSalesChannel(value: string | null | undefined): value is SalesChannel {
  return value !== null && value !== undefined && value in SALES_CHANNEL_META;
}
